/** The Subscriptions search namespaces (notification subscriptions).
 *
 * Same QuerySearch contract as /api-requests/, but over
 * Integration_Notification_Subscription — a SMALL table, so unlike the
 * webhook log there is no mandatory time window and unbounded searches are
 * fine. `type` is the canonical notification type (BOOKING_UPDATE, …),
 * `remote` the vendor-side subscription id, `credential` the API_Credential
 * fingerprint bound to the subscription.
 *
 * Operators per namespace are a SUBSET of the spec's `x-namespace-operators`
 * (NotificationSubscriptionFilterNode — contract-tested).
 */
import type { NamespaceConfigMap } from '@/components/QuerySearch';
import { makeSuggestionFetcher } from '@/api/bookings';

import {
  SUBSCRIPTION_MODES,
  SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_TYPES,
} from './subscriptionFormat';

/** Build a local (no-API) suggestion fetcher from a fixed label list. */
function localLabels(labels: string[]) {
  return (query: string) =>
    Promise.resolve(
      labels
        .filter((s) => s.toLowerCase().includes(query.toLowerCase()))
        .map((s) => ({ value: s, label: s })),
    );
}

export function buildSubscriptionNamespaces(): NamespaceConfigMap {
  return {
    tenant: {
      label: 'tenant',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: makeSuggestionFetcher('tenant'),
      help: 'Tour operator (tenant) by code; an all-digits value also matches the tenant id.',
      examples: ['tenant=curatedplanet', 'tenant=42'],
    },
    restech: {
      label: 'restech',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Booking-system codename the subscription belongs to (equals, case-insensitive).',
      examples: ['restech=ventrata'],
    },
    account: {
      label: 'account',
      operators: ['iexact'],
      valueType: 'string',
      help: 'Account slug (equals, case-insensitive). The default account has an empty slug.',
      examples: ['account=uk-office'],
    },
    type: {
      label: 'type',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(SUBSCRIPTION_TYPES),
      help: 'Canonical notification type (equals, case-insensitive).',
      examples: ['type=BOOKING_UPDATE'],
    },
    status: {
      label: 'status',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(SUBSCRIPTION_STATUSES),
      help: 'Subscription lifecycle status. Failed rows (create_failed / delete_failed) keep their last error.',
      examples: ['status=active', 'status=create_failed'],
    },
    mode: {
      label: 'mode',
      operators: ['iexact'],
      valueType: 'string',
      fetchSuggestions: localLabels(SUBSCRIPTION_MODES),
      help: 'Delivery mode: log_only records deliveries without processing (the rollout ramp); process feeds the import pipeline.',
      examples: ['mode=log_only'],
    },
    remote: {
      label: 'remote',
      operators: ['exact'],
      valueType: 'string',
      help: 'Vendor-side subscription id (exact).',
      examples: ['remote=sub_0a1b2c3d'],
    },
    credential: {
      label: 'credential',
      operators: ['exact'],
      valueType: 'string',
      help: 'API_Credential key fingerprint bound to the subscription (exact).',
      examples: ['credential=b1946ac92492'],
    },
    created: {
      label: 'created',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the subscription was created, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['created=2026-08-20', 'created="2026-08-01 00:00".."2026-08-27 18:00"'],
    },
    last_received: {
      label: 'last_received',
      operators: ['exact', 'range'],
      valueType: 'datetime',
      help: 'When the last notification delivery arrived, UTC. `=` matches a whole day; a range takes date-time bounds.',
      examples: ['last_received=2026-08-26', 'last_received="2026-08-20 00:00".."2026-08-27 00:00"'],
    },
    id: {
      label: 'id',
      operators: ['exact'],
      valueType: 'string',
      help: 'Subscription integer id (exact).',
      examples: ['id=123'],
    },
  };
}
